import React from "react";
import {
  Box,
  Flex,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  useToast,
  Spinner,
  keyframes,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { loginUser, storeUserData } from "action/loginAction";
import { storage } from "utils/commonUtils";
import { useHistory } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LogoEdunara from "assets/img/Edunara-Logo.png";
import { API_BASE_URL } from "utils/apiClient";

const float1 = keyframes`0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-20px) rotate(5deg)}`;
const float2 = keyframes`0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(16px) rotate(-4deg)}`;
const float3 = keyframes`0%,100%{transform:translateY(0)}33%{transform:translateY(-12px)}66%{transform:translateY(8px)}`;
const fadeUp = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}`;
const fadeRight = keyframes`from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:translateX(0)}`;
const shake = keyframes`0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}`;

function FloatingField({ label, type, value, onChange, error, isDisabled, children }) {
  const [focused, setFocused] = React.useState(false);
  const active = focused || value.length > 0;

  return (
    <Box mb={2}>
      <Box
        position="relative"
        borderRadius="12px"
        border="1.5px solid"
        borderColor={error ? "red.500" : focused ? "#2563eb" : "#e2e0f0"}
        bg={focused ? "rgba(37,99,235,0.02)" : "#fafafe"}
        transition="border-color 0.2s, box-shadow 0.2s"
        boxShadow={focused ? `0 0 0 4px ${error ? "rgba(239,68,68,0.12)" : "rgba(37,99,235,0.1)"}` : "none"}
      >
        <Box
          as="label"
          position="absolute"
          left="16px"
          top={active ? "8px" : "50%"}
          transform={active ? "none" : "translateY(-50%)"}
          fontSize={active ? "11px" : "15px"}
          fontWeight={active ? 600 : 400}
          color={error ? "red.500" : focused ? "#2563eb" : "#9ca3af"}
          transition="all 0.18s cubic-bezier(0.4,0,0.2,1)"
          pointerEvents="none"
          letterSpacing={active ? "0.04em" : "normal"}
          textTransform={active ? "uppercase" : "none"}
          zIndex={1}
        >
          {label}
        </Box>
        <InputGroup size="md">
          <Input
            type={type}
            value={value}
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            isDisabled={isDisabled}
            pt={active ? "24px" : "16px"}
            pb={active ? "8px" : "16px"}
            pl="16px"
            pr={children ? "48px" : "16px"}
            fontSize="15px"
            fontWeight={500}
            color="#1e1b4b"
            bg="transparent"
            border="none"
            _focus={{ boxShadow: "none", border: "none" }}
            _placeholder={{ display: "none" }}
          />
          {children && (
            <InputRightElement height="100%" right="14px">
              {children}
            </InputRightElement>
          )}
        </InputGroup>
      </Box>
      {error && (
        <Text fontSize="12px" color="red.500" mt={1} ml={1} fontWeight={500}>
          {error}
        </Text>
      )}
    </Box>
  );
}

function SignIn() {
  React.useEffect(() => {
    document.title = "Sign In | LMS Tenant";
  }, []);

  const toast = useToast();
  const history = useHistory();
  const { login, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = React.useState({ email: "", password: "", rememberMe: false });
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [shakeIt, setShakeIt] = React.useState(false);
  const [stats, setStats] = React.useState({ total_tenants: null, total_users: null, total_students: null });

  React.useEffect(() => {
    fetch(`${API_BASE_URL}/public-stats`)
      .then((r) => r.json())
      .then((json) => { if (json?.data) setStats(json.data); })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) history.push("/admin/dashboard");
  }, [isAuthenticated, history]);

  React.useEffect(() => {
    try {
      const remembered = storage.getRememberedEmail();
      if (remembered) setFormData((p) => ({ ...p, email: remembered, rememberMe: true }));
    } catch {}
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validateForm = () => {
    const e = {};
    if (!formData.email.trim()) e.email = t("Email is required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = t("Enter a valid email address");
    if (!formData.password.trim()) e.password = t("Password is required");
    else if (formData.password.length < 6) e.password = t("Password must be at least 6 characters");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) {
      setShakeIt(true);
      setTimeout(() => setShakeIt(false), 600);
      return;
    }
    setLoading(true);
    try {
      const result = await loginUser(formData.email, formData.password);
      if (result.success) {
        if (result.token) {
          const stored = storeUserData(result.data, result.token, formData.rememberMe, formData.email);
          if (!stored) {
            toast({ title: t("Storage Error"), description: t("Failed to save login data."), status: "error", duration: 3000, isClosable: true });
            return;
          }
        }
        login(result.data, result.token);
        toast({ title: t("Login Successful"), description: t("Welcome back!"), status: "success", duration: 3000, isClosable: true });
        history.push("/admin/dashboard");
      } else {
        setShakeIt(true);
        setTimeout(() => setShakeIt(false), 600);
        toast({ title: t("Login Failed"), description: result.message || t("Invalid credentials."), status: "error", duration: 5000, isClosable: true });
      }
    } catch {
      toast({ title: t("Login Error"), description: t("An error occurred. Please try again."), status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex h="100vh" w="100%" overflow="hidden">
      {/* Left Brand Panel */}
      <Box
        flex="0 0 46%"
        position="relative"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="flex-start"
        px={{ base: "32px", lg: "52px" }}
        py="56px"
        bg="linear-gradient(145deg, #0f172a 0%, #1e3a5f 55%, #2563eb 100%)"
        display={{ base: "none", lg: "flex" }}
      >
        {/* Decorative circles */}
        <Box position="absolute" top="-80px" right="-80px" w="320px" h="320px" borderRadius="50%" bg="rgba(255,255,255,0.05)" animation={`${float1} 8s ease-in-out infinite`} />
        <Box position="absolute" bottom="-60px" left="-60px" w="260px" h="260px" borderRadius="50%" bg="rgba(255,255,255,0.06)" animation={`${float2} 10s ease-in-out infinite`} />
        <Box position="absolute" top="55%" right="-30px" w="140px" h="140px" borderRadius="50%" bg="rgba(255,255,255,0.04)" animation={`${float3} 7s ease-in-out infinite`} />
        <Box position="absolute" top="25%" left="70%" w="60px" h="60px" borderRadius="50%" bg="rgba(96,165,250,0.25)" animation={`${float2} 5s ease-in-out infinite`} />
        <Box position="absolute" bottom="30%" right="15%" w="90px" h="90px" borderRadius="50%" border="2px solid rgba(255,255,255,0.08)" />
        <Box position="absolute" top="15%" left="8%" w="50px" h="50px" borderRadius="50%" border="1.5px solid rgba(255,255,255,0.1)" />
        {/* Dot grid */}
        <Box position="absolute" inset={0} bgImage="radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)" bgSize="28px 28px" pointerEvents="none" />

        {/* Logo */}
        <Box position="relative" zIndex={2} mb={10} animation={`${fadeRight} 0.5s ease both`}>
          <Box as="img" src={LogoEdunara} h="52px" filter="brightness(0) invert(1)" objectFit="contain" alt="Edunara" />
        </Box>

        {/* Headline */}
        <Box position="relative" zIndex={2} animation={`${fadeRight} 0.5s ease both 0.1s`}>
          <Text fontSize="32px" fontWeight={800} color="white" lineHeight={1.2} letterSpacing="-0.02em" mb={3}>
            {t("Smart Learning,")}<br />
            <Box as="span" color="#60a5fa">{t("Unlimited Growth.")}</Box>
          </Text>
          <Text fontSize="15px" color="rgba(255,255,255,0.65)" lineHeight={1.7} maxW="300px">
            {t("Manage tenants, monitor subscriptions, and oversee all platform activity — in one place.")}
          </Text>
        </Box>

        {/* Stats */}
        <Flex position="relative" zIndex={2} gap={7} mt={11} animation={`${fadeRight} 0.5s ease both 0.2s`}>
          {[
            [stats.total_tenants, t("Tenants")],
            [stats.total_users, t("Users")],
            [stats.total_students, t("Students")],
          ].map(([val, lab]) => (
            <Box key={lab}>
              <Text fontSize="22px" fontWeight={800} color="white" letterSpacing="-0.02em">
                {val == null ? "—" : val.toLocaleString()}
              </Text>
              <Text fontSize="12px" color="rgba(255,255,255,0.5)" fontWeight={500} mt={1}>{lab}</Text>
            </Box>
          ))}
        </Flex>

        {/* Tenant badge */}
        <Box position="absolute" bottom="28px" left="52px" zIndex={2}>
          <Flex align="center" gap={2} bg="rgba(255,255,255,0.1)" backdropFilter="blur(8px)" border="1px solid rgba(255,255,255,0.15)" borderRadius="99px" px="14px" py="6px">
            <Box w="8px" h="8px" borderRadius="50%" bg="#60a5fa" />
            <Text fontSize="12px" color="rgba(255,255,255,0.8)" fontWeight={600} letterSpacing="0.04em">LMS Tenant</Text>
          </Flex>
        </Box>
      </Box>

      {/* Right Form Panel */}
      <Flex
        flex={1}
        direction="column"
        justify="center"
        align="center"
        px={{ base: "24px", md: "56px" }}
        py="48px"
        bg="white"
        position="relative"
        overflowY="auto"
      >
        {/* Mobile logo */}
        <Box display={{ base: "block", lg: "none" }} mb={6}>
          <Box as="img" src={LogoEdunara} h="40px" objectFit="contain" alt="Edunara" />
        </Box>

        <Box w="100%" maxW="380px" animation={`${fadeUp} 0.55s ease`}>
          {/* Greeting */}
          <Box mb={9}>
            <Text fontSize="28px" fontWeight={800} color="#1e1b4b" letterSpacing="-0.025em" mb={1.5}>
              {t("Welcome back")}
            </Text>
            <Text fontSize="14px" color="#9ca3af" lineHeight={1.6}>
              {t("Sign in to")} <Box as="span" color="#6b7280" fontWeight={600}>LMS Tenant</Box> {t("to continue your learning journey.")}
            </Text>
          </Box>

          {/* Form */}
          <Box
            as="form"
            onSubmit={handleSubmit}
            noValidate
            animation={shakeIt ? `${shake} 0.5s ease` : "none"}
          >
            <Flex direction="column" gap={3.5}>
              <FloatingField
                label={t("Email address")}
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                error={errors.email}
                isDisabled={loading}
              />
              <FloatingField
                label={t("Password")}
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                error={errors.password}
                isDisabled={loading}
              >
                <IconButton
                  aria-label={showPassword ? t("Hide password") : t("Show password")}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  size="sm"
                  variant="ghost"
                  color="#9ca3af"
                  onClick={() => setShowPassword((v) => !v)}
                  isDisabled={loading}
                  _hover={{ bg: "transparent" }}
                  _focus={{ boxShadow: "none" }}
                />
              </FloatingField>
            </Flex>

            {/* Remember + Forgot */}
            <Flex justify="space-between" align="center" mt={4} mb={6}>
              <Flex align="center" gap={2} cursor="pointer" onClick={() => !loading && handleInputChange("rememberMe", !formData.rememberMe)}>
                <Flex
                  w="18px"
                  h="18px"
                  borderRadius="5px"
                  border={`2px solid ${formData.rememberMe ? "#2563eb" : "#d1d5db"}`}
                  bg={formData.rememberMe ? "#2563eb" : "white"}
                  align="center"
                  justify="center"
                  transition="all 0.15s"
                  flexShrink={0}
                >
                  {formData.rememberMe && (
                    <Box as="svg" w="10px" h="10px" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </Box>
                  )}
                </Flex>
                <Text fontSize="13px" color="#6b7280" fontWeight={500}>{t("Remember me")}</Text>
              </Flex>
            </Flex>

            {/* Submit */}
            <Button
              type="submit"
              w="100%"
              h="50px"
              isDisabled={loading}
              isLoading={loading}
              loadingText={t("Signing in...")}
              spinner={<Spinner size="sm" mr={2} />}
              bg={loading ? "#d1d5db" : "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)"}
              color="white"
              fontSize="15px"
              fontWeight={700}
              letterSpacing="0.01em"
              borderRadius="16px"
              boxShadow={loading ? "none" : "0 6px 20px rgba(37,99,235,0.35)"}
              _hover={{ transform: loading ? "none" : "translateY(-1px)" }}
              _active={{ transform: "none" }}
              transition="all 0.2s"
            >
              {t("Sign In")}
            </Button>
          </Box>

          {/* Footer */}
          <Text textAlign="center" mt={9} fontSize="12px" color="#c4c0d8" fontWeight={500}>
            © {new Date().getFullYear()} PT Zegen Solusi Mandiri · All Rights Reserved
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

export default SignIn;
